import React from 'react';
import { 
  ArrowLeft, 
  ArrowRight, 
  Award, 
  Sparkles,
  Gauge,
  TrendingUp
} from 'lucide-react';
import { Language } from '../types';
import { translations } from '../data/translations';

interface HeroProps {
  lang: Language;
  onOpenCalculator: () => void;
  onExplorePortfolio: () => void;
}

export const Hero: React.FC<HeroProps> = ({ lang, onOpenCalculator, onExplorePortfolio }) => {
  const t = translations[lang];
  const ArrowIcon = lang === 'fa' ? ArrowLeft : ArrowRight;

  const techBadges = [
    { name: 'React 19 & Next.js 15', color: 'text-amber-200 border-amber-500/30 bg-amber-500/10' },
    { name: 'WordPress 6.7 Custom', color: 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10' },
    { name: 'TYPO3 Enterprise LTS', color: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
    { name: 'Joomla 5 Portals', color: 'text-amber-300 border-amber-500/30 bg-amber-500/10' },
    { name: 'Technical SEO & Schemas', color: 'text-yellow-400 border-yellow-500/30 bg-yellow-500/10' },
    { name: 'WooCommerce High-Scale', color: 'text-amber-400 border-amber-500/30 bg-amber-500/10' },
    { name: 'Node.js & Python API', color: 'text-yellow-300 border-yellow-500/30 bg-yellow-500/10' },
    { name: 'Tailwind CSS 4', color: 'text-amber-200 border-amber-500/30 bg-amber-500/10' },
  ];

  return (
    <section
      id="hero-section"
      className="relative py-20 lg:py-24 overflow-hidden bg-gradient-to-b from-[#0e0704] via-[#160c07] to-[#090402] border-b border-amber-900/40"
    >
      {/* Immersive Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-grid-pattern opacity-50 pointer-events-none -z-10" />

      {/* Ambient background glows */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[380px] bg-amber-600/15 rounded-full blur-[150px] pointer-events-none -z-10" />
      <div className="absolute top-1/3 right-10 w-[500px] h-[300px] bg-yellow-600/10 rounded-full blur-[140px] pointer-events-none -z-10" />
      <div className="absolute bottom-10 left-10 w-[450px] h-[300px] bg-amber-800/20 rounded-full blur-[140px] pointer-events-none -z-10" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-6 text-center">
          
          {/* Top Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30 text-amber-300 text-xs font-semibold shadow-lg shadow-amber-500/10 backdrop-blur-md">
            <Sparkles className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
            <span>{t.hero.badge}</span>
            <span className="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>
            <span className="font-mono text-[11px] text-amber-400/80">sorena-it.ir</span>
          </div>

          {/* Main H1 Headline */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white leading-[1.25]">
            <span className="block">{t.hero.title1}</span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-400">
              {t.hero.title2}
            </span>
          </h1>

          {/* Descriptive Body */}
          <p className="text-stone-300 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto font-normal">
            {t.hero.description}
          </p>

          {/* CTA Buttons */}
          <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              id="hero-calculator-cta"
              onClick={onOpenCalculator}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-yellow-300 text-stone-950 font-bold text-sm sm:text-base shadow-xl shadow-amber-600/30 hover:shadow-amber-500/50 transition-all duration-200 transform hover:-translate-y-0.5 cursor-pointer"
            >
              <span>{t.hero.ctaPrimary}</span>
              <ArrowIcon className="w-4 h-4 text-stone-950" />
            </button>

            <button
              id="hero-portfolio-cta"
              onClick={onExplorePortfolio}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-[#22120a]/80 hover:bg-[#321a0d] text-amber-100 hover:text-white font-semibold text-sm sm:text-base border border-amber-800/60 hover:border-amber-500/60 transition-all duration-200 backdrop-blur-md cursor-pointer"
            >
              <span>{t.hero.ctaSecondary}</span>
            </button>
          </div>

          {/* Quick Guarantees / Key Metrics */}
          <div className="pt-6 grid grid-cols-3 gap-3 border-t border-amber-900/40 max-w-xl mx-auto text-start">
            <div className="p-3.5 rounded-xl bg-[#1c0e08]/70 backdrop-blur-md border border-amber-900/50">
              <div className="flex items-center gap-1.5 text-amber-300 text-xs font-semibold mb-1">
                <Gauge className="w-3.5 h-3.5 text-yellow-400" />
                <span className="truncate">{t.hero.speedMetric}</span>
              </div>
              <div className="text-sm sm:text-base font-extrabold text-white">
                {t.hero.speedValue}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-[#1c0e08]/70 backdrop-blur-md border border-amber-900/50">
              <div className="flex items-center gap-1.5 text-yellow-300 text-xs font-semibold mb-1">
                <TrendingUp className="w-3.5 h-3.5 text-yellow-400" />
                <span className="truncate">{t.hero.seoMetric}</span>
              </div>
              <div className="text-sm sm:text-base font-extrabold text-white">
                {t.hero.seoValue}
              </div>
            </div>

            <div className="p-3.5 rounded-xl bg-[#1c0e08]/70 backdrop-blur-md border border-amber-900/50">
              <div className="flex items-center gap-1.5 text-amber-300 text-xs font-semibold mb-1">
                <Award className="w-3.5 h-3.5 text-amber-400" />
                <span className="truncate">{t.hero.satisfactionMetric}</span>
              </div>
              <div className="text-sm sm:text-base font-extrabold text-white">
                {t.hero.satisfactionValue}
              </div>
            </div>
          </div>
        </div>

        {/* Continuous Tech Stack Badge Strip */}
        <div className="mt-16 pt-8 border-t border-amber-900/40">
          <div className="text-center text-xs font-semibold text-amber-300/80 mb-4 tracking-wider">
            {t.hero.techTickerTitle}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
            {techBadges.map((badge, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-3.5 py-1.5 rounded-xl border border-amber-800/50 bg-[#22120a]/70 text-amber-200 text-xs font-medium transition-all hover:scale-105 hover:border-amber-500/50 backdrop-blur-sm"
              >
                {badge.name}
              </span>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};
